import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { REFERRAL_OP_ASSET_TYPE_ID, type MemberLedgerSummaryDto } from '@pokerroom/types';
import { PackagesTab } from './PackagesTab';
import { PointsLedgerTab } from './PointsLedgerTab';
import { VouchersTab } from './VouchersTab';
import { ZipaTab } from './ZipaTab';
import { timedLabel, type LedgerTab } from './ledgerTabs';

/** Index a tab-bar key moves focus to (WAI-ARIA tabs: arrows wrap, Home/End jump), or null. */
export function tabKeyTarget(key: string, index: number, count: number): number | null {
  if (key === 'ArrowRight') return (index + 1) % count;
  if (key === 'ArrowLeft') return (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

/** Horizontal tab bar (scrolls on a phone) and the panel of the active tab. */
export function MemberLedgerTabs({
  memberId,
  tabs,
  active,
  summary,
  onSelect,
  info,
  locked = false,
  summaryUnavailable = false,
}: {
  memberId: string;
  tabs: LedgerTab[];
  active: LedgerTab;
  summary: MemberLedgerSummaryDto | undefined;
  summaryUnavailable?: boolean;
  onSelect: (tab: LedgerTab) => void;
  info: ReactNode;
  /** A member who is not active: wallet tabs show records without actions. */
  locked?: boolean;
}) {
  const { t } = useTranslation('member');
  const timedPeriods = tabs.flatMap((tab) => (tab.period ? [tab.period] : []));
  const label = (tab: LedgerTab) => {
    switch (tab.kind) {
      case 'ASSET': {
        const asset = summary?.assets?.find((row) => row.assetTypeId === tab.assetTypeId);
        return `${asset?.displayName ?? (tab.assetTypeId === REFERRAL_OP_ASSET_TYPE_ID ? 'OP' : '')}${tab.period ? ` ${tab.period}` : ''}`;
      }
      case 'info':
        return t('ledgerTabInfo');
      case 'PLAYER':
        return (
          summary?.pointTypes?.find((type) => type.code === 'PLAYER')?.displayName ??
          t('ledgerTabPlayer')
        );
      case 'TIMED':
        if (summary?.pointTypes?.find((type) => type.code === 'TIMED'))
          return `${summary.pointTypes.find((type) => type.code === 'TIMED')!.displayName} ${tab.period}`;
        return t(
          timedLabel(tab.period!, timedPeriods).key,
          timedLabel(tab.period!, timedPeriods).values,
        );
      case 'Z':
        return (
          summary?.pointTypes?.find((type) => type.code === 'Z')?.displayName ?? t('ledgerTabZ')
        );
      case 'ZIPA':
        return t('ledgerTabZipa');
      case 'PACKAGES':
        return t('walletTabPackages');
      case 'VOUCHERS':
        return t('walletTabVouchers');
    }
  };
  const month =
    active.kind === 'TIMED' ? summary?.timed.find((m) => m.period === active.period) : undefined;

  const listRef = useRef<HTMLDivElement>(null);
  // Keep the selected tab inside the scrolling bar without moving the page.
  useEffect(() => {
    const list = listRef.current;
    const tab = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !tab) return;
    const listBox = list.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    if (tabBox.left < listBox.left) list.scrollLeft -= listBox.left - tabBox.left;
    else if (tabBox.right > listBox.right) list.scrollLeft += tabBox.right - listBox.right;
  }, [active.key]);
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const target = tabKeyTarget(event.key, index, tabs.length);
    if (target === null) return;
    event.preventDefault();
    onSelect(tabs[target]!);
    listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[target]?.focus();
  };

  const asset = summary?.assets?.find((row) => row.assetTypeId === active.assetTypeId);
  const assetPeriod = asset?.periodBalances.find((row) => row.period === active.period);
  return (
    <div>
      <div
        ref={listRef}
        role="tablist"
        aria-label={t('ledgerTabsLabel')}
        className="flex gap-1 overflow-x-auto [scrollbar-width:thin] [scrollbar-color:var(--color-base-300)_transparent] border-b border-base-300 mb-4"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.key}
            id={`member-tab-${tab.key}`}
            role="tab"
            type="button"
            aria-selected={tab.key === active.key}
            aria-controls="member-tabpanel"
            tabIndex={tab.key === active.key ? 0 : -1}
            onClick={() => onSelect(tab)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`min-h-11 shrink-0 px-4 text-sm whitespace-nowrap border-b-2 ${
              tab.key === active.key
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-base-content/70 hover:text-base-content'
            }`}
          >
            {label(tab)}
          </button>
        ))}
      </div>
      <div
        id="member-tabpanel"
        role="tabpanel"
        aria-labelledby={`member-tab-${active.key}`}
        tabIndex={0}
        className="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {active.kind === 'info' && info}
        {active.kind === 'PLAYER' && summary && (
          <PointsLedgerTab
            key={`${memberId}:PLAYER`}
            memberId={memberId}
            kind="PLAYER"
            balance={summary.player}
          />
        )}
        {active.kind === 'Z' && summary && (
          <PointsLedgerTab
            memberId={memberId}
            key={`${memberId}:Z`}
            kind="Z"
            balance={summary.z.balance}
            deductOnly={!summary.z.isZCustomer}
          />
        )}
        {active.kind === 'TIMED' && month && (
          <PointsLedgerTab
            key={`${memberId}:${month.period}`}
            memberId={memberId}
            kind="TIMED"
            period={month.period}
            balance={month.balance}
            expiresAt={month.expiresAt}
            expired={month.expired}
          />
        )}
        {active.kind === 'ASSET' && asset && (
          <PointsLedgerTab
            key={active.key}
            memberId={memberId}
            assetTypeId={asset.assetTypeId}
            unit={asset.unit}
            enabled={asset.enabled && !locked}
            opManualConfiguration={summary?.opManualConfiguration}
            memberLocked={locked}
            summaryUnavailable={summaryUnavailable}
            balance={assetPeriod?.balance ?? asset.balance}
            period={active.period}
            expiresAt={assetPeriod?.expiresAt}
            expired={assetPeriod?.expired}
          />
        )}
        {active.kind === 'ASSET' && !asset && active.assetTypeId === REFERRAL_OP_ASSET_TYPE_ID && (
          <PointsLedgerTab
            memberId={memberId}
            assetTypeId={REFERRAL_OP_ASSET_TYPE_ID}
            balance="0"
            enabled={false}
            opManualConfiguration={summary?.opManualConfiguration}
            memberLocked={locked}
            summaryUnavailable={summaryUnavailable}
          />
        )}
        {active.kind === 'ZIPA' && <ZipaTab memberId={memberId} />}
        {active.kind === 'PACKAGES' && <PackagesTab memberId={memberId} locked={locked} />}
        {active.kind === 'VOUCHERS' && <VouchersTab memberId={memberId} locked={locked} />}
      </div>
    </div>
  );
}
